import { setContext } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { from as rxFrom, switchMap } from 'rxjs';
import { refreshAccessToken, refreshTokenIfNeeded } from './token-refresh';
import { TOKEN_KEY } from './auth-session';

function retryWithRefresh(
  operation: Parameters<ErrorLink.ErrorHandler>[0]['operation'],
  forward: Parameters<ErrorLink.ErrorHandler>[0]['forward'],
) {
  return rxFrom(refreshAccessToken()).pipe(
    switchMap((newToken) => {
      // refreshAccessToken handles its own clearSessionAndRedirect on failure;
      // throw here just aborts this in-flight operation cleanly.
      if (!newToken) throw new Error('Session expired');
      const oldHeaders = operation.getContext().headers;
      operation.setContext({
        headers: { ...oldHeaders, authorization: `JWT ${newToken}` },
      });
      return forward(operation);
    }),
  );
}

/**
 * Retries once with a refreshed token when a request fails with an auth error.
 */
export const createErrorLink = () =>
  new ErrorLink(({ error, operation, forward }) => {
    if (operation?.operationName === 'Login') return;

    if (ServerError.is(error) && error.statusCode === 401) {
      return retryWithRefresh(operation, forward);
    }

    if (CombinedGraphQLErrors.is(error)) {
      const hasAuthError = error.errors.some((err) => {
        const message = err.message?.toLowerCase() || '';
        const code = (err.extensions as { code?: string } | undefined)?.code || '';
        return (
          message.includes('authentication') ||
          message.includes('unauthorized') ||
          message.includes('not authenticated') ||
          code === 'UNAUTHENTICATED' ||
          code === 'FORBIDDEN'
        );
      });
      if (hasAuthError) return retryWithRefresh(operation, forward);
    }
  });

/**
 * Attaches the JWT to outgoing requests, refreshing proactively when near expiry.
 * Login operations never send a token.
 */
export const createAuthLink = () =>
  setContext(async (operation, { headers }) => {
    if (operation?.operationName === 'Login') {
      return { headers: { ...headers, authorization: '' } };
    }
    const token = (await refreshTokenIfNeeded()) || localStorage.getItem(TOKEN_KEY);
    return {
      headers: {
        ...headers,
        authorization: token ? `JWT ${token}` : '',
      },
    };
  });
